import { faChevronCircleDown, faChevronCircleUp, faDownload, faFile, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect } from 'react'

import moment from 'moment'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const PPQViewRequest = ({ viewingRequest, onUpdate }) => {
    const [activityLogItems, setActivityLogItems] = useState(null)
    const [requestItems, setRequestItems] = useState(null)
    const [isAccordionOpen, setAccordionOpen] = useState(false)
    const [isResending, setIsResending] = useState(false)

    const { createInfo } = useAuth()
    const axios = useAxiosPrivate()

    useEffect(() => {
        if (viewingRequest?.activity_log) {
            const activityLog =
                typeof viewingRequest.activity_log === 'string'
                    ? JSON.parse(viewingRequest.activity_log)
                    : viewingRequest.activity_log

            setActivityLogItems(activityLog)
        }

        if (viewingRequest?.request_information) {
            const requestInfo =
                typeof viewingRequest.request_information === 'string'
                    ? JSON.parse(viewingRequest.request_information)
                    : viewingRequest.request_information

            setRequestItems(requestInfo)
        }
    }, [viewingRequest])

    const toggleAccordion = () => {
        setAccordionOpen(!isAccordionOpen)
    }

    const statusClass = (status) => {
        if (typeof status !== 'string') {
            return 'bg-gray-200 text-gray-800'
        }

        switch (status.toLowerCase()) {
            case 'form completed - awaiting result':
            case 'no barrier to work':
            case 'no action required':
                return 'bg-green-200 text-green-800 border-green-400'
            case 'awaiting form completion':
            case 'referral needed':
            case 'in progress':
                return 'bg-amber-200 text-amber-800 border-amber-400'
            case 'closed':
            case 'form expired - start date lapsed':
            case 'referral cancelled':
                return 'bg-rose-200 text-rose-800 border-rose-400'
            default:
                if (status.toLowerCase().startsWith('form completed -')) {
                    return 'bg-green-200 text-green-800 border-green-400'
                } else {
                    return 'bg-gray-200 text-gray-800'
                }
        }
    }

    const handleDownload = async () => {
        try {
            let fileName = requestItems.filename.replace('/pdf/oh-result/', '')
            window
                .open(
                    `${process.env.REACT_APP_KIS}/download-decrypted-file/` + fileName,
                    '_blank'
                )
                .focus()
        } catch (error) {
            console.error('Error downloading the file', error)
        }
    }

    const handleResendPPQ = async () => {
        setIsResending(true)
        try {
            const response = await axios.get('/resend-ppq-form/' + viewingRequest.id)
            createInfo('success', `PEHC form resent successfully`)
            if (onUpdate) {
                onUpdate()
            }
        } catch (error) {
            console.error('Failed to resend PPQ form:', error)
        } finally {
            setIsResending(false)
        }
    }

    return (
        <div className="space-y-6 pb-5 pt-6">
            <div className="space-y-2">
                <p>
                    <strong>Reference: </strong>
                    {viewingRequest?.request_info_array?.claim_reference || 'N/A'}
                </p>
                <p>
                    <strong>Title: </strong> Pre-Placement Questionnaire
                </p>
                <p>
                    <strong>Description: </strong>
                    {viewingRequest?.description || 'N/A'}
                </p>
                <p>
                    <strong>Status: </strong>
                    <span
                        className={`${statusClass(
                            viewingRequest?.request_status
                        )} rounded-full px-2 py-1 capitalize font-medium text-sm`}
                    >
                        {viewingRequest?.request_status || ''}
                    </span>
                </p>
                <p className="text-wrap">
                    <strong>Additional Information: </strong>
                    <div className="ml-4">
                        <strong>Staff Name:</strong>{' '}
                        {viewingRequest?.request_info_array?.staff_details?.staff_name || 'N/A'}
                    </div>
                    <div className="ml-4">
                        <strong>Start Date:</strong>{' '}
                        {viewingRequest?.request_info_array?.staff_details?.start_date
                            ? moment(viewingRequest.request_info_array.staff_details.start_date).format('DD/MM/YYYY')
                            : 'N/A'}
                    </div>
                    <div className="ml-4">
                        <strong>Role Applied For:</strong>{' '}
                        {viewingRequest?.request_info_array?.staff_details?.role_applied_for || 'N/A'}
                    </div>
                    <div className="ml-4">
                        <strong>Email:</strong>{' '}
                        {viewingRequest?.request_info_array?.staff_details?.staff_email || 'N/A'}
                    </div>
                </p>

                <div>
                    <div className="mb-4 flex items-center justify-between">
                        <div className="mb-4 flex items-center">
                            <strong>Activity Log</strong>
                            <button
                                onClick={toggleAccordion}
                                className="ml-2 text-indigo-900 hover:underline"
                            >
                                <FontAwesomeIcon
                                    icon={isAccordionOpen ? faChevronCircleUp : faChevronCircleDown}
                                    className="h-4 w-4 focus:border-none"
                                />
                            </button>
                        </div>
                        {viewingRequest?.request_status === 'Awaiting Form Completion' && (
                            <button
                                type="button"
                                onClick={handleResendPPQ}
                                className="px-2 py-1 bg-indigo-500 text-white rounded-md cursor-pointer hover:bg-indigo-600 flex items-center gap-2"
                                disabled={isResending}
                            >
                                {isResending && (
                                    <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4" />
                                )}
                                Resend PEHC Form
                            </button>
                        )}
                    </div>
                    {isAccordionOpen && (
                        <div className="pl-6">
                            {activityLogItems &&
                                (Array.isArray(activityLogItems)
                                    ? activityLogItems.length > 0
                                    : Object.keys(activityLogItems).length > 0) ? (
                                <ol className="relative border-s">
                                    {(Array.isArray(activityLogItems)
                                        ? activityLogItems
                                        : [activityLogItems]
                                    ).map((log, index) => (
                                        <li className="mb-6 flex items-center" key={index}>
                                            <span className="absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                            <div className="ml-6">
                                                <h3 className="mb-1 text-base font-semibold text-gray-900">
                                                    {log.title}
                                                </h3>
                                                <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                                    {moment(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                                                </time>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <ol className="relative border-s ">
                                    <li className="mb-10 ms-6">
                                        <span className="absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                        <h3 className="flex items-center mb-1 text-base font-semibold text-gray-900">
                                            No Activity Log Data
                                        </h3>
                                        <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                            {moment().format('DD/MM/YYYY HH:mm:ss')}
                                        </time>
                                    </li>
                                </ol>
                            )}
                        </div>
                    )}
                </div>

                {requestItems?.filename && (
                    <div className="flex items-center justify-between border border-teal-400 bg-teal-50 rounded-md px-4 py-2">
                        <div className="flex items-center">
                            <FontAwesomeIcon
                                icon={faFile}
                                className="h-4 w-4 text-[#02ADAD] pr-2"
                            />
                            <div className="text-gray-800">PPQ Results Document</div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon
                                icon={faDownload}
                                className="text-[#02ADAD] hover:text-teal-700 cursor-pointer"
                                onClick={handleDownload}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PPQViewRequest